import { useState } from "react";
import type { PublicPhoto } from "../api/photos";
import { PhotoCard } from "./PhotoCard";
import { Lightbox } from "./Lightbox";
import { Button } from "../design-system/Button";
import { buildPhotoFilename, extensionFromUrl } from "../utils/downloadFilename";
import { savePhotos } from "../utils/savePhotos";
import styles from "./PhotoGrid.module.css";

interface PhotoGridProps {
  photos: PublicPhoto[];
  coupleNames: string;
  weddingDate: string;
  onLightboxOpenChange?: (open: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

export function PhotoGrid({ photos, coupleNames, weddingDate, onLightboxOpenChange, onSavingChange }: PhotoGridProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  function open(index: number) {
    setOpenIndex(index);
    onLightboxOpenChange?.(true);
  }

  function close() {
    setOpenIndex(null);
    onLightboxOpenChange?.(false);
  }

  function toggleSelectionMode() {
    setSelectionMode((mode) => !mode);
    setSelected(new Set());
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSaveSelected() {
    // photos is newest-first; chronological (upload order) numbering runs oldest -> 1.
    const items = photos
      .map((photo, index) => ({ photo, chronologicalIndex: photos.length - index }))
      .filter(({ photo }) => selected.has(photo.id))
      .map(({ photo, chronologicalIndex }) => ({
        url: photo.originalUrl,
        filename: buildPhotoFilename(coupleNames, weddingDate, chronologicalIndex, extensionFromUrl(photo.originalUrl)),
      }));

    setSaving(true);
    onSavingChange?.(true);
    try {
      await savePhotos(items);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  }

  if (photos.length === 0) {
    return <p className={styles.empty}>Aucune photo pour le moment — sois le premier à partager un souvenir !</p>;
  }

  return (
    <>
      <div className={styles.toolbar}>
        <Button variant="secondary" size="small" onClick={toggleSelectionMode}>
          {selectionMode ? "Annuler la sélection" : "Sélectionner des photos"}
        </Button>
        {selectionMode && (
          <Button size="small" disabled={selected.size === 0 || saving} onClick={() => void handleSaveSelected()}>
            {saving ? "Enregistrement…" : `Enregistrer (${selected.size})`}
          </Button>
        )}
      </div>

      <div className={styles.grid}>
        {photos.map((photo, index) => (
          <PhotoCard
            key={photo.id}
            photo={photo}
            selectionMode={selectionMode}
            selected={selected.has(photo.id)}
            onClick={() => (selectionMode ? toggleSelected(photo.id) : open(index))}
          />
        ))}
      </div>
      {openIndex !== null && (
        <Lightbox
          photos={photos}
          index={openIndex}
          coupleNames={coupleNames}
          weddingDate={weddingDate}
          onClose={close}
          onNavigate={setOpenIndex}
          onSavingChange={onSavingChange}
        />
      )}
    </>
  );
}
